/**
 * Custom hook for managing linkedTable editor state
 * Consolidates all state management for the linkedTable editor
 */

import { useState, useMemo, useCallback } from 'react';

import { toast } from '@/lib/toast';
import type {
  LinkedTable,
  LinkedTableFilter,
  LinkedTableCalculatedColumn,
  LinkedTableRelationship,
  LinkedTableValue,
  LinkedTableRowGroupSort,
  LinkedTableVariable,
} from '@/lib/types';

/**
 * State for tracking original values (for change detection)
 */
interface OriginalState {
  linkedTableName: string;
  linkedTableDescription: string;
  filters: LinkedTableFilter[];
  calculated_columns: LinkedTableCalculatedColumn[];
  relationships: LinkedTableRelationship[];
  row_groups: string[];
  row_group_sorts: LinkedTableRowGroupSort[];
  values: LinkedTableValue[];
  variables: LinkedTableVariable[];
}

export const useLinkedTableState = (isCreateMode: boolean) => {
  // LinkedTable metadata
  const [linkedTable, setLinkedTable] = useState<LinkedTable | null>(null);

  // Current editable state
  const [linkedTableName, setLinkedTableName] = useState<string>('');
  const [linkedTableDescription, setLinkedTableDescription] = useState<string>('');
  const [filters, setFilters] = useState<LinkedTableFilter[]>([]);
  const [calculated_columns, setCalculatedColumns] = useState<
    LinkedTableCalculatedColumn[]
  >([]);
  const [relationships, setRelationships] = useState<LinkedTableRelationship[]>([]);
  const [row_groups, setRowGroups] = useState<string[]>([]);
  const [row_group_sorts, setRowGroupSorts] = useState<LinkedTableRowGroupSort[]>([]);
  const [values, setValues] = useState<LinkedTableValue[]>([]);
  const [variables, setVariables] = useState<LinkedTableVariable[]>([]);

  // Version selections per table alias (unified - no base/relationship distinction)
  const [versionSelections, setVersionSelections] = useState<Record<string, string>>({});

  // Original state (for change detection)
  const [originalState, setOriginalState] = useState<OriginalState>({
    linkedTableName: '',
    linkedTableDescription: '',
    filters: [],
    calculated_columns: [],
    relationships: [],
    row_groups: [],
    row_group_sorts: [],
    values: [],
    variables: [],
  });

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calculate number of pending changes
   * In create mode, returns 1 if there's a linkedTable name (indicating saveable state)
   * In edit mode, compares current state to original state
   */
  const pendingChangesCount = useMemo(() => {
    if (isCreateMode) {
      // In create mode, consider it "changed" if we have a name
      // This allows the save button to be enabled
      return linkedTableName.trim() !== '' ? 1 : 0;
    }

    let changes = 0;

    if (linkedTableName !== originalState.linkedTableName) changes++;
    if (linkedTableDescription !== originalState.linkedTableDescription) changes++;
    if (JSON.stringify(filters) !== JSON.stringify(originalState.filters))
      changes++;
    if (
      JSON.stringify(calculated_columns) !==
      JSON.stringify(originalState.calculated_columns)
    )
      changes++;
    if (
      JSON.stringify(relationships) !==
      JSON.stringify(originalState.relationships)
    )
      changes++;
    // Note: Version selections are NOT tracked as changes because they are query parameters,
    // not linkedTable configuration. They don't modify the linkedTable definition itself.
    if (
      JSON.stringify(row_groups) !==
      JSON.stringify(originalState.row_groups)
    )
      changes++;
    if (
      JSON.stringify(row_group_sorts) !==
      JSON.stringify(originalState.row_group_sorts)
    )
      changes++;
    if (JSON.stringify(values) !== JSON.stringify(originalState.values))
      changes++;
    if (JSON.stringify(variables) !== JSON.stringify(originalState.variables))
      changes++;

    return changes;
  }, [
    isCreateMode,
    linkedTableName,
    linkedTableDescription,
    filters,
    calculated_columns,
    relationships,
    row_groups,
    row_group_sorts,
    values,
    variables,
    originalState,
  ]);

  /**
   * Update original state to current state (after save)
   */
  const syncOriginalState = useCallback(() => {
    setOriginalState({
      linkedTableName,
      linkedTableDescription,
      filters,
      calculated_columns,
      relationships,
      row_groups,
      row_group_sorts,
      values,
      variables,
    });
  }, [
    linkedTableName,
    linkedTableDescription,
    filters,
    calculated_columns,
    relationships,
    row_groups,
    row_group_sorts,
    values,
    variables,
  ]);

  /**
   * Reset all changes to original values
   */
  const resetToOriginal = useCallback(() => {
    if (isCreateMode) return;

    setLinkedTableName(originalState.linkedTableName);
    setLinkedTableDescription(originalState.linkedTableDescription);
    setFilters(originalState.filters);
    setCalculatedColumns(originalState.calculated_columns);
    setRelationships(originalState.relationships);
    setRowGroups(originalState.row_groups);
    setRowGroupSorts(originalState.row_group_sorts);
    setValues(originalState.values);
    setVariables(originalState.variables);

    toast.linkedTable.changesReverted();
  }, [isCreateMode, originalState]);

  /**
   * Initialize linkedTable state from loaded linkedTable data
   */
  const initializeFromLinkedTable = useCallback((linkedTableData: unknown) => {
    const linkedTable = linkedTableData as LinkedTable;

    // Set basic linkedTable metadata
    setLinkedTable(linkedTable);
    setLinkedTableName(linkedTable.name);
    setLinkedTableDescription(linkedTable.description || '');

    // Set linkedTable configuration
    setFilters(linkedTable.filters || []);
    setCalculatedColumns(linkedTable.calculated_columns || []);

    // Convert SQL-native joins to frontend relationships format
    const convertedRelationships: LinkedTableRelationship[] = [];
    if (linkedTable.joins && Array.isArray(linkedTable.joins) && linkedTable.tables && Array.isArray(linkedTable.tables)) {
      for (const join of linkedTable.joins) {
        // Find the right table (the one being joined)
        const rightTable = linkedTable.tables.find(t => t.alias === join.right_table);
        if (rightTable) {
          convertedRelationships.push({
            id: join.id,
            source_table_alias: join.left_table,
            target_type: rightTable.type as 'table' | 'linked_table',
            target_id: rightTable.target_id,
            alias: rightTable.alias,
            join_type: (join.join_type?.toUpperCase() || 'LEFT') as 'LEFT' | 'INNER' | 'RIGHT' | 'FULL',
            field_mappings: join.conditions?.map(cond =>
              cond.left_formula
                ? {
                    mode: 'formula' as const,
                    source_field_id: '',
                    target_field_id: '',
                    source_formula: cond.left_formula,
                    target_formula: cond.right_formula || '',
                  }
                : {
                    mode: 'field' as const,
                    source_field_id: cond.left_field || '',
                    target_field_id: cond.right_field || '',
                  }
            ) || [],
          });
        }
      }
    }
    setRelationships(convertedRelationships);
    setRowGroups(linkedTable.row_groups || []);
    setRowGroupSorts(linkedTable.row_group_sorts || []);
    setValues(linkedTable.values || []);
    setVariables(linkedTable.variables || []);

    // Initialize version selections with 'current' for all tables
    const syncedVersionSelections: Record<string, string> = {};
    if (linkedTable.tables && Array.isArray(linkedTable.tables)) {
      for (const table of linkedTable.tables) {
        syncedVersionSelections[table.alias] = 'current';
      }
    }
    setVersionSelections(syncedVersionSelections);

    // Set original state snapshot for change detection
    setOriginalState({
      linkedTableName: linkedTable.name,
      linkedTableDescription: linkedTable.description || '',
      filters: linkedTable.filters || [],
      calculated_columns: linkedTable.calculated_columns || [],
      relationships: convertedRelationships,
      row_groups: linkedTable.row_groups || [],
      row_group_sorts: linkedTable.row_group_sorts || [],
      values: linkedTable.values || [],
      variables: linkedTable.variables || [],
    });
  }, []);

  /**
   * Update version selection for a specific table alias
   */
  const updateVersionSelection = useCallback((alias: string, version: string) => {
    setVersionSelections(prev => ({
      ...prev,
      [alias]: version,
    }));
  }, []);

  return {
    // LinkedTable data
    linkedTable,
    setLinkedTable,

    // Current state
    linkedTableName,
    setLinkedTableName,
    linkedTableDescription,
    setLinkedTableDescription,
    filters,
    setFilters,
    calculated_columns,
    setCalculatedColumns,
    relationships,
    setRelationships,
    row_groups,
    setRowGroups,
    row_group_sorts,
    setRowGroupSorts,
    values,
    setValues,
    variables,
    setVariables,

    // Version selections (unified for all tables)
    versionSelections,
    setVersionSelections,
    updateVersionSelection,

    // Loading and error
    loading,
    setLoading,
    error,
    setError,

    // Computed
    pendingChangesCount,

    // Actions
    syncOriginalState,
    resetToOriginal,
    initializeFromLinkedTable,
  };
};
